// SCR-STAFF-870 (AP-INV-01؛ US-INV-001 تا 003، 030): فهرست کالاها با جستجو (کد، نام، نام تجاری، بارکد/GTIN)، نوع و وضعیت؛ اسکن GS1 یا بارکد
// ← کالای موجود (و پیش‌پر بچ) یا تعریف کالای تازه با GTIN اسکن‌شده؛ دسته‌ها؛ نشان «نمونه» برای کالای بسته تخصصی.
import AddIcon from '@mui/icons-material/Add';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type InvItemType, type InvItemView, type InvScanResolution } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, ListPage, SampleBadge, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type KeyboardEvent } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import { WriteNotice } from '../settings/common';
import { CategoriesDialog } from './CategoriesDialog';
import { ItemDialog } from './ItemDialog';
import { inventoryKeys, ITEM_TYPES, ITEMS_PATH } from './inventoryCommon';
import { looksLikeGs1, normalizeGtin, parseGs1 } from './gs1';

export const SCR_STAFF_870 = 'SCR-STAFF-870';

/** مسیر جزئیات کالا با متن اسکن برای پیش‌پر فرم بچ. */
export function itemPath(id: string, scan?: string): string {
  return scan ? `${ITEMS_PATH}/${id}?scan=${encodeURIComponent(scan)}` : `${ITEMS_PATH}/${id}`;
}

export function ItemsPage() {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('inv.item.manage');
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [type, setType] = useState<InvItemType | ''>('');
  const [active, setActive] = useState<'true' | 'false' | ''>('true');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [creating, setCreating] = useState<{ gtin?: string; scan?: string } | null>(null);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [scan, setScan] = useState('');
  const [scanMessage, setScanMessage] = useState<string | null>(null);

  const items = useQuery({
    queryKey: [...inventoryKeys.items(session?.tenantId), q, type, active, cursor],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/inventory/items', {
          params: { query: { q: q.trim() || undefined, type: type || undefined, active: active === '' ? undefined : active === 'true', cursor, limit: 50 } },
        }),
      ),
  });

  const resolve = useMutation({
    mutationFn: (raw: string) => unwrap(tenantApi.POST('/api/v1/inventory/scans:resolve', { body: { raw } })),
    onSuccess: (result: InvScanResolution, raw) => {
      setScan('');
      if (result.item) {
        navigate(itemPath(result.item.itemId, result.isGs1 ? raw : undefined));
        return;
      }
      if (write.canWrite) {
        const gtin = result.gs1?.gtin ?? normalizeGtin(result.code) ?? undefined;
        setCreating({ gtin, scan: result.isGs1 ? raw : undefined });
        setScanMessage(null);
      } else {
        setScanMessage('inv:scan.not_found');
      }
    },
    onError: () => setScanMessage('inv:scan.invalid'),
  });

  const onScanKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || !scan.trim()) {
      return;
    }
    event.preventDefault();
    setScanMessage(null);
    if (looksLikeGs1(scan) && !parseGs1(scan).ok) {
      setScanMessage('inv:scan.invalid');
      return;
    }
    resolve.mutate(scan);
  };

  const columns: DataTableColumn<InvItemView>[] = [
    {
      id: 'name',
      headerKey: 'inv:items.columns.name',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <Link component={RouterLink} to={itemPath(row.id)} data-testid={`item-link-${row.code}`}>
            {row.name}
          </Link>
          {row.isSample && <SampleBadge />}
        </Stack>
      ),
    },
    { id: 'code', headerKey: 'inv:items.columns.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    { id: 'type', headerKey: 'inv:items.columns.type', cell: (row) => t(`inv:item_type.${row.type}`) },
    { id: 'unit', headerKey: 'inv:items.columns.unit', cell: (row) => t(`inv:unit.${row.baseUnit}`) },
    {
      id: 'tracking',
      headerKey: 'inv:items.columns.tracking',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {row.tracksBatch && <Chip size="small" variant="outlined" label={t('inv:items.tracking.batch')} />}
          {row.tracksExpiry && <Chip size="small" variant="outlined" label={t('inv:items.tracking.expiry')} />}
          {row.isMultiUse && <Chip size="small" variant="outlined" label={t('inv:items.tracking.multi_use')} />}
        </Stack>
      ),
    },
    { id: 'batches', headerKey: 'inv:items.columns.batches', cell: (row) => (row.tracksBatch ? faDigits(Number(row.activeBatchCount)) : '—') },
    {
      id: 'status',
      headerKey: 'inv:items.columns.status',
      cell: (row) => <StatusChip tone={row.isActive ? 'active' : 'inactive'} labelKey={row.isActive ? 'inv:status.active' : 'inv:status.inactive'} />,
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_870}
      titleKey="inv:items.title"
      subtitleKey="inv:items.subtitle"
      actions={
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<CategoryOutlinedIcon />} onClick={() => setCategoriesOpen(true)} data-testid="item-categories">
            {t('inv:categories.button')}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating({})} disabled={!write.canWrite} data-testid="item-create">
            {t('inv:items.add')}
          </Button>
        </Stack>
      }
      filters={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          <TextField
            size="small"
            fullWidth
            label={t('inv:scan.label')}
            helperText={t('inv:scan.help')}
            value={scan}
            onChange={(event) => setScan(event.target.value)}
            onKeyDown={onScanKey}
            slotProps={{
              input: { startAdornment: <InputAdornment position="start"><QrCodeScannerIcon /></InputAdornment> },
              htmlInput: { dir: 'ltr', 'data-testid': 'item-scan' },
            }}
          />
          {scanMessage && (
            <Alert severity="warning" data-testid="item-scan-message">
              {t(scanMessage)}
            </Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              size="small"
              label={t('inv:items.search')}
              value={q}
              onChange={(event) => {
                setCursor(undefined);
                setQ(event.target.value);
              }}
              slotProps={{ htmlInput: { 'data-testid': 'item-search' } }}
              sx={{ minWidth: 240 }}
            />
            <TextField
              select
              size="small"
              label={t('inv:items.filters.type')}
              value={type}
              onChange={(event) => {
                setCursor(undefined);
                setType(event.target.value as InvItemType | '');
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">{t('inv:items.filters.all')}</MenuItem>
              {ITEM_TYPES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`inv:item_type.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={t('inv:items.filters.status')}
              value={active}
              onChange={(event) => {
                setCursor(undefined);
                setActive(event.target.value as 'true' | 'false' | '');
              }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">{t('inv:items.filters.all')}</MenuItem>
              <MenuItem value="true">{t('inv:status.active')}</MenuItem>
              <MenuItem value="false">{t('inv:status.inactive')}</MenuItem>
            </TextField>
          </Stack>
        </Stack>
      }
    >
      <DataTable
        captionKey="inv:items.caption"
        columns={columns}
        rows={items.data?.items}
        getRowId={(row) => row.id}
        isLoading={items.isPending}
        error={items.error}
        onRetry={() => void items.refetch()}
        hasMore={Boolean(items.data?.nextCursor)}
        onLoadMore={() => setCursor(items.data?.nextCursor ?? undefined)}
        emptyTitleKey="inv:items.empty"
        emptyDescriptionKey="inv:items.empty_hint"
      />
      {creating && (
        <ItemDialog
          item={null}
          initial={creating.gtin ? { gtin: creating.gtin } : undefined}
          onClose={() => setCreating(null)}
          onSaved={(saved) => navigate(itemPath(saved.id, creating.scan))}
        />
      )}
      {categoriesOpen && <CategoriesDialog onClose={() => setCategoriesOpen(false)} />}
    </ListPage>
  );
}
